import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as DeleteIcon } from '../../../../assets/delete-red.svg';
import renameIcon from '../../../../assets/pen.svg';
import CustomAlert from '../../../../components/shared/CustomAlert/CustomAlert';
import CustomButton from '../../../../components/shared/CustomButton/CustomButton';
import CustomDeleteModal from '../../../../components/shared/CustomDeleteModal/CustomDeleteModal';
import CustomInputModal from '../../../../components/shared/CustomInputModal/CustomInputModal';
import { initialAlertState, useAlertStatus } from '../../../../customHook/useAlertStatus';
import { Role, TableType } from '../../../../model/rolesAndPerms.model';
import { modalReducer } from '../../../../reducers/modalReducer';
import RoutePaths from '../../../../routes/RoutePaths';
import { AdminService } from '../../../../service/admin.service';
import zeplinColor from '../../../../theme/Color';
import { transformPermissionsToTables } from '../../../../utilities/rolesAndPermsUtils';
import { PermissionTable } from './PermissionTable.tsx';

export const SelectedRoleAndPermission = () => {
  const navigate = useNavigate();
  const roleId = useLocation().pathname.split('/').at(-1);
  const [roleDetails, setRoleDetails] = useState<Role>();
  const [loading, setLoading] = useState(false);
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    rename: false,
    delete: false,
  });

  const [tables, setTables] = useState<TableType[]>([]);
  const [allRawPermissions, setAllRawPermissions] = useState<any[]>([]);

  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoading(true);
        if (!roleId) return;

        // Fetch Both: All possible perms and current Role perms
        const [allPermsRes, roleRes] = await Promise.all([
          AdminService.getAllPermissions(),
          AdminService.getRoleDetails(roleId),
        ]);

        setAllRawPermissions(allPermsRes.permissions);

        const roleData = roleRes.role;
        setRoleDetails(roleData);

        // Transform: Merge Schema with Role Values
        const formattedTables = transformPermissionsToTables(
          allPermsRes.permissions,
          roleData.permissions, // These are the perms assigned to the role
        );

        setTables(formattedTables);
      } catch (error) {
        console.error('Error loading permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [roleId]);

  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const handleSave = async () => {
    try {
      startOperation('upload', 'Saving permission changes...');
      const selectedPermissionIds: string[] = [];

      tables.forEach((table) => {
        table.rows.forEach((row) => {
          row.values.forEach((isChecked, index) => {
            if (isChecked) {
              const actionNames = ['view', 'create', 'edit', 'delete'];
              const action = actionNames[index];

              const match = allRawPermissions.find(
                (p) =>
                  p.module === table.columns[0].name && p.name === row.name && p.action === action,
              );

              if (match) selectedPermissionIds.push(match.permissionId);
            }
          });
        });
      });

      await AdminService.updateRolePermissions(roleId!, selectedPermissionIds);

      completeOperation('upload', 'Permissions updated successfully');
    } catch (error) {
      completeOperation('upload', 'Failed to save permissions', true);
    }
  };

  const fetchRoleDetails = useCallback(async () => {
    try {
      setLoading(true);
      if (!roleId) return;
      const data = await AdminService.getRoleDetails(roleId);
      setRoleDetails(data.role);

      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  }, [roleId]);

  const onRenameClick = async (newName: string) => {
    try {
      startOperation('rename', 'Updating name...');
      modalDispatch({ type: 'CLOSE', payload: 'rename' });
      await AdminService.updateRole(roleId!, { roleName: newName });
      fetchRoleDetails();

      completeOperation('rename', 'Updated name successfully');
    } catch (error: any) {
      completeOperation('rename', `Update Failed: ${error.message}`, true);
    }
  };

  const onDeleteClick = async () => {
    try {
      startOperation('delete', 'Deleting role...');
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      await AdminService.deleteRole(roleId!);

      navigate(`${RoutePaths.Home}/admin`);
      completeOperation('delete', 'Role deleted successfully');
    } catch (error) {
      completeOperation('delete', `Deletion Failed: ${error}`, true);
    }
  };

  useEffect(() => {
    fetchRoleDetails();
  }, [fetchRoleDetails, roleId]);

  if (loading || !roleDetails) {
    return (
      <Box
        sx={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'scroll',
          height: 'calc(100vh - 80px)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Skeleton
              variant='text'
              sx={{
                width: '20%',
                height: '48px',
              }}
            />
            <Skeleton variant='circular' sx={{ width: '28px', height: '28px' }} />
          </Box>
          <Skeleton
            variant='text'
            sx={{
              width: '40%',
            }}
          />
        </Box>

        <Skeleton
          variant='rounded'
          sx={{
            marginTop: '24px',
            height: '50%',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'scroll',
        height: 'calc(100vh - 80px)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography variant='heading1'>{roleDetails.roleName}</Typography>
            <IconButton
              sx={{ width: '28px', height: '28px' }}
              onClick={(e) => {
                e.stopPropagation();
                modalDispatch({ type: 'OPEN', payload: 'rename' });
              }}
            >
              <img src={renameIcon} alt='rename icon' width={'24px'} height={'24px'} />
            </IconButton>
          </Box>
          <Typography variant='body3'>
            Customize access levels for this role by enabling or disabling permissions for each
            module.
          </Typography>
        </Box>
        <Box>
          <CustomButton
            disabled={false}
            variant='text'
            text='Delete Role'
            onClick={() => {
              modalDispatch({ type: 'OPEN', payload: 'delete' });
            }}
            iconPosition='end'
            icon={<DeleteIcon />}
            style={{
              borderRadius: '14px',
              height: '48px',
              padding: '0px 24px',
              color: zeplinColor.Error,
            }}
            buttonVariant='buttonMedium'
          />
          <CustomButton
            disabled={false}
            variant='contained'
            text='Save'
            onClick={handleSave}
            style={{
              borderRadius: '14px',
              height: '48px',
              padding: '0px 24px',
            }}
            buttonVariant='buttonMedium'
          />
        </Box>
      </Box>

      {tables.map((table) => (
        <PermissionTable key={table.id} table={table} />
      ))}

      <CustomInputModal
        modalType={'rename'}
        title={'Rename Role'}
        label='Role name'
        isModalOpen={modalState.rename}
        modalDispatch={modalDispatch}
        handleAction={onRenameClick}
        inputValue={roleDetails.roleName}
      />

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />

      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={onDeleteClick}
        title='Delete Role'
        description={`You’re about to delete the role “${roleDetails.roleName}” This action will remove all permissions associated with it. Are you sure you want to delete?`}
      />
    </Box>
  );
};
